import React, { type ReactElement, type ReactNode, useMemo } from 'react'
import { type Palette, Typography, useTheme } from '@mui/material'
import { useAppSelector } from 'store/hooks'
import { Role } from 'common/utils/types'

import { VendorStatus } from '../vendor.types'

import styles from './VendorProfileLayout.module.scss'

export const statusColors = (vendorStatus: VendorStatus, palette: Palette): string => {
  switch (vendorStatus) {
    case VendorStatus.activated:
      return palette.charts.orange400.main
    case VendorStatus.additional_documents:
    case VendorStatus.pending_documents:
      return palette.charts.yellow.main
    case VendorStatus.pending:
      return palette.charts.orange400.main
    case VendorStatus.verified:
      return palette.charts.green200.main
    case VendorStatus.pendingVerification:
      return palette.charts.orange400.main
    case VendorStatus.rejected:
      return palette.charts.red.main
    default:
      return 'inherit'
  }
}

export const statusMessage = (vendorStatus: VendorStatus, options: { header?: boolean } = {}): string => {
  switch (vendorStatus) {
    case VendorStatus.activated:
      return 'U obradi'
    case VendorStatus.additional_documents:
    case VendorStatus.pending_documents:
      return options.header ? 'Dokumenta' : 'Dodatna dokumenta'
    case VendorStatus.pendingVerification:
      return 'U obradi'
    case VendorStatus.verified:
      return 'Verifikovan'
    case VendorStatus.rejected:
      return 'Odbijen'
    default:
      return ''
  }
}

interface VendorProfileLayoutProps {
  children?: ReactNode
}

const VendorProfileLayout: React.FC<VendorProfileLayoutProps> = ({ children }): ReactElement => {
  const { palette } = useTheme()
  const { vendor } = useAppSelector((state) => state.vendor)
  const { userInfo } = useAppSelector((state) => state.auth)

  const isAdmin = useMemo<boolean>(() => userInfo?.role === Role.admin ?? false, [userInfo?.role])
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.nameWrapper}>
          <Typography variant="title1" className={styles.name}>
            {(vendor?.user?.role === Role.consultant && vendor?.user?.isVendor) ||
            (isAdmin && vendor?.user?.role === Role.consultant)
              ? vendor?.user
                ? `${vendor.user.name?.first ?? ''} ${vendor.user.name?.last ?? ''}`
                : '-'
              : vendor?.name ?? '-'}
          </Typography>
        </div>
        <div className={styles.status}>
          {vendor?.status !== VendorStatus.verified && 'Status verifikacije:'}
          <div className={styles.statusInfo}>
            {vendor?.status && (
              <Typography variant="title1" sx={{ color: statusColors(vendor.status, palette) }}>
                {statusMessage(vendor?.status, { header: true })}
              </Typography>
            )}
          </div>
        </div>
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  )
}

export default VendorProfileLayout
